/**
 * Lengths as people type and read them. Everything is stored in metres in the hall frame;
 * feet and inches (to the nearest eighth of an inch) or metres are only for people.
 */

export const FT = 0.3048;
export const IN = 0.0254;

export type UnitSystem = 'imperial' | 'metric';
export type LengthUnit = 'ft' | 'in' | 'm' | 'cm' | 'mm';

const FACTOR: Record<LengthUnit, number> = { ft: FT, in: IN, m: 1, cm: 0.01, mm: 0.001 };

const FRACTION_GLYPHS: Record<string, string> = {
  '½': '1/2', '¼': '1/4', '¾': '3/4', '⅛': '1/8', '⅜': '3/8', '⅝': '5/8', '⅞': '7/8',
};
const EIGHTHS = ['', '⅛', '¼', '⅜', '½', '⅝', '¾', '⅞'];
const MINUS = '\u2212';

const NUM = String.raw`(\d+(?:\.\d+)?(?:[ -]\d+/\d+)?|\d+/\d+|\.\d+)`;
const FT_WORD = String.raw`(?:ft\.?|feet|foot|')`;
const IN_WORD = String.raw`(?:in\.?|inch(?:es)?|")`;
const UNIT_WORDS: [LengthUnit, string][] = [
  ['ft', FT_WORD],
  ['in', IN_WORD],
  ['mm', String.raw`(?:mm|millimet(?:re|er)s?)`],
  ['cm', String.raw`(?:cm|centimet(?:re|er)s?)`],
  ['m', String.raw`(?:m|met(?:re|er)s?)`],
];
const ANY_UNIT = UNIT_WORDS.map(([, w]) => w).join('|');

const FEET_INCHES = new RegExp(String.raw`^(-)?\s*${NUM}\s*${FT_WORD}\s*${NUM}\s*${IN_WORD}?$`);
const SINGLE = new RegExp(String.raw`^(-)?\s*${NUM}\s*(${ANY_UNIT})?$`);

function normalise(text: string): string {
  let t = text.trim().toLowerCase();
  t = t.replace(/[’‘′´`]/g, "'").replace(/[”“″]/g, '"').replace(/''/g, '"');
  for (const [glyph, frac] of Object.entries(FRACTION_GLYPHS)) {
    t = t.replace(new RegExp(String.raw`(\d)\s*${glyph}`, 'g'), `$1 ${frac}`).replaceAll(glyph, frac);
  }
  t = t.replace(/(\d),(?=\d{3}(?!\d))/g, '$1').replaceAll(MINUS, '-');
  return t.replace(/\s+/g, ' ');
}

function parseNumber(s: string): number | null {
  const mixed = /^(\d+(?:\.\d+)?)[ -](\d+)\/(\d+)$/.exec(s);
  if (mixed) {
    const [, whole, num, den] = mixed;
    return Number(den) === 0 ? null : Number(whole) + Number(num) / Number(den);
  }
  const frac = /^(\d+)\/(\d+)$/.exec(s);
  if (frac) return Number(frac[2]) === 0 ? null : Number(frac[1]) / Number(frac[2]);
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function unitOf(word: string | undefined): LengthUnit | null {
  if (!word) return null;
  for (const [unit, pattern] of UNIT_WORDS) {
    if (new RegExp(`^${pattern}$`).test(word)) return unit;
  }
  return null;
}

/**
 * Metres from what someone typed, or null when it isn't a length.
 *
 * Accepts `6 ft`, `6'`, `13'5"`, `13' 5`, `13 ft 5 in`, `27.4 ft`, `30 in`, `30"`, `5 1/2 in`,
 * `73' 0⅝"`, `2.1 m`, `210 cm`, `2100 mm`, and a bare number in the field's own unit.
 * `13'5` means 13 ft 5 in. Anything else (two units of different systems, stray words) is refused
 * rather than guessed.
 */
export function parseLength(text: string, defaultUnit: LengthUnit = 'ft'): number | null {
  if (typeof text !== 'string') return null;
  const t = normalise(text);
  if (!t) return null;
  const fi = FEET_INCHES.exec(t);
  if (fi) {
    const feet = parseNumber(fi[2]!);
    const inches = parseNumber(fi[3]!);
    if (feet === null || inches === null) return null;
    const m = feet * FT + inches * IN;
    return fi[1] ? -m : m;
  }
  const one = SINGLE.exec(t);
  if (!one) return null;
  const value = parseNumber(one[2]!);
  if (value === null) return null;
  const unit = one[3] ? unitOf(one[3]) : defaultUnit;
  if (!unit) return null;
  const m = value * FACTOR[unit];
  return one[1] ? -m : m;
}

export const LENGTH_HINT = 'Type a length such as 6 ft, 13\'5", 27.4 ft, 30 in or 2.1 m';

export interface FormatOptions {
  /** `auto` shows inches below 4 ft ("30 in") and feet and inches above ("7 ft 4 in"). */
  style?: 'auto' | 'ft-in' | 'in';
}

/** Feet and inches to the nearest eighth of an inch: "73 ft 0⅝ in", "5 ft", "30 in", "4⅛ in". */
export function formatImperial(metres: number, options: FormatOptions = {}): string {
  const style = options.style ?? 'auto';
  const eighths = Math.round((Math.abs(metres) / IN) * 8);
  const sign = metres < 0 && eighths > 0 ? MINUS : '';
  const totalInches = Math.floor(eighths / 8);
  const frac = EIGHTHS[eighths % 8];
  if (style === 'in' || (style === 'auto' && eighths < 4 * 12 * 8)) {
    return `${sign}${totalInches}${frac} in`;
  }
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;
  if (inches === 0 && !frac) return `${sign}${feet} ft`;
  return `${sign}${feet} ft ${inches}${frac} in`;
}

/** Metres to the centimetre: "22.27 m", "76 cm". */
export function formatMetric(metres: number): string {
  const cm = Math.round(Math.abs(metres) * 100);
  const sign = metres < 0 && cm > 0 ? MINUS : '';
  if (cm < 100) return `${sign}${cm} cm`;
  return `${sign}${(cm / 100).toFixed(2)} m`;
}

export function formatLength(metres: number, system: UnitSystem, options: FormatOptions = {}): string {
  return system === 'metric' ? formatMetric(metres) : formatImperial(metres, options);
}

/** The unit a bare number typed into a field means, for each unit system. */
export function fieldUnit(system: UnitSystem, small = false): LengthUnit {
  if (system === 'metric') return small ? 'cm' : 'm';
  return small ? 'in' : 'ft';
}
